import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { QueryClient } from '@tanstack/react-query';
import { createRouter, Link } from '@tanstack/react-router';
import { LogoMobileZephyrIcon } from '@ui/custom-icons';
import { Button } from '@ui/primitives/button';
import { DangerIcon, ErrorContent, ErrorPageLayout } from '@ui/primitives/error-page';
import { GlobeErrorPage } from '@ui/primitives/globe-error';
import { AxiosError } from 'axios';
import { routeTree } from './routeTree.gen';
export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 10,
            retry (failureCount, error) {
                // Skip retry requests on Not Found, Forbidden & Bad Request
                if (error instanceof AxiosError && (error.status === 404 || error.status === 403 || error.status === 400)) {
                    return false;
                }
                // Preserve TanStack Query's default behavior: retry up to 3 times
                return failureCount < 3;
            }
        }
    }
});
/**
 * Global error component used as the default catch-all for any route that doesn't define its own errorComponent.
 * Renders inside the existing layout (sidebar, header, etc.) since it only catches errors below the root route.
 */ function DefaultErrorComponent(param) {
    let { error, reset } = param;
    const isDev = process.env.NODE_ENV === 'development';
    return /*#__PURE__*/ _jsx("div", {
        className: "flex h-full w-full items-center justify-center p-8",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "w-full max-w-lg space-y-6 text-center",
            children: [
                /*#__PURE__*/ _jsx(DangerIcon, {
                    className: "mx-auto h-12 w-12"
                }),
                /*#__PURE__*/ _jsx("h1", {
                    className: "text-2xl font-bold text-destructive",
                    children: "Something went wrong"
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "text-sm text-muted-foreground",
                    children: "An unexpected error occurred. You can try again or go back to the home page."
                }),
                isDev && error.message && /*#__PURE__*/ _jsx("pre", {
                    className: "max-h-40 overflow-auto rounded-md border bg-muted p-3 text-left text-xs text-muted-foreground",
                    children: error.message
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center justify-center gap-3",
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            variant: "outline",
                            onClick: reset,
                            children: "Try again"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            asChild: true,
                            children: /*#__PURE__*/ _jsx(Link, {
                                to: "/",
                                children: "Go home"
                            })
                        })
                    ]
                })
            ]
        })
    });
}
/** Root-level not found component. Renders standalone (no layout) since it's on the __root route. */ export function DefaultNotFoundComponent() {
    return /*#__PURE__*/ _jsx(ErrorPageLayout, {
        children: /*#__PURE__*/ _jsx(ErrorContent, {
            icon: DangerIcon,
            title: "404",
            subtitle: "Page not found",
            description: "The page you're looking for doesn't exist or has been moved.",
            image: /*#__PURE__*/ _jsx("div", {
                className: "w-full max-w-[300px]",
                children: /*#__PURE__*/ _jsx(GlobeErrorPage, {})
            }),
            action: /*#__PURE__*/ _jsx(Button, {
                asChild: true,
                children: /*#__PURE__*/ _jsx(Link, {
                    to: "/",
                    children: "Go to Home Page"
                })
            })
        })
    });
}
// Set up a Router instance
export const router = createRouter({
    routeTree,
    context: {
        queryClient
    },
    defaultErrorComponent: DefaultErrorComponent,
    defaultPendingComponent: ()=>{
        return /*#__PURE__*/ _jsx("div", {
            className: "flex w-full items-center justify-center p-8",
            style: {
                height: "calc(100vh - 180px)",
                overflow: 'hidden'
            },
            children: /*#__PURE__*/ _jsx(LogoMobileZephyrIcon, {
                className: "h-12 w-12"
            })
        });
    },
    // This make the loader only wait 200ms before showing the pending component, instead of the default 1000ms
    defaultPendingMs: 200,
    defaultPreload: 'intent',
    // Since we're using React Query, we don't want loader calls to ever be stale
    // This will ensure that the loader is always called when the route is preloaded or visited
    defaultPreloadStaleTime: 0,
    scrollRestoration: true
});
